import { BoardApiAction } from 'actions/BoardApiAction'
import { BoardItemParams } from 'models/BoardItem'
import { Board } from 'models/Board'
import { Resource } from 'models/Resource'
import { useExtensionContext } from 'hooks/ExtensionContext'
import React, { useCallback, useMemo, useReducer, useRef } from 'react'
import { SubsChannelProducts } from 'models/SubsChannelProducts'
import { SubsApiAction } from 'actions/SubsApiAction'
import { useOneShot } from 'hooks/useOneShot'

type BoardState = {
    userId: string
    isLogin: boolean
    subsChannelProducts?: SubsChannelProducts
    resource: Resource<Board>
    isLoadingMore: boolean
}

const ACTION_GET = 'board/get' as const
const ACTION_GET_SUCCESS = 'board/get/success' as const
const ACTION_GET_FAILURE = 'board/get/failure' as const
const ACTION_AUTHORIZE = 'board/authorize' as const
const ACTION_AUTHORIZE_SUCCESS = 'board/authorize/success' as const
const ACTION_AUTHORIZE_FAILURE = 'board/authorize/failure' as const
const ACTION_LOAD_MORE = 'board/loadMore' as const
const ACTION_LOAD_MORE_SUCCESS = 'board/loadMore/success' as const
const ACTION_LOAD_MORE_FAILURE = 'board/loadMore/failure' as const


const get = () => {
    return { type: ACTION_GET }
}

const getSuccess = (board: BoardItemParams[], subsChannelProducts?: SubsChannelProducts) => {
    return { type: ACTION_GET_SUCCESS, board, subsChannelProducts }
}

const getFailure = (error: unknown) => {
    return { type: ACTION_GET_FAILURE, error }
}

const authorize = () => {
    return { type: ACTION_AUTHORIZE }
}

const authorizeSuccess = (board: BoardItemParams[]) => {
    return { type: ACTION_AUTHORIZE_SUCCESS, board }
}

const authorizeFailure = (error: unknown) => {
    return { type: ACTION_AUTHORIZE_FAILURE, error }
}

const loadMore = () => {
    return { type: ACTION_LOAD_MORE }
}

const loadMoreSuccess = (items: BoardItemParams[]) => {
    return { type: ACTION_LOAD_MORE_SUCCESS, items }
}

const loadMoreFailure = (error: unknown) => {
    return { type: ACTION_LOAD_MORE_FAILURE, error }
}

type Action = ReturnType<typeof get> 
    | ReturnType<typeof getSuccess>
    | ReturnType<typeof getFailure>
    | ReturnType<typeof authorize> 
    | ReturnType<typeof authorizeSuccess>
    | ReturnType<typeof authorizeFailure>
    | ReturnType<typeof loadMore>
    | ReturnType<typeof loadMoreSuccess>
    | ReturnType<typeof loadMoreFailure>

const reducer: React.Reducer<BoardState, Action> = (state, action) => {
    switch(action.type) {
        case ACTION_GET:
            return { ...state, resource: Resource.loading() }
        case ACTION_GET_SUCCESS:
            return { ...state, resource: Resource.of(Board.init(action.board, false)), subsChannelProducts: action.subsChannelProducts }
        case ACTION_GET_FAILURE:
            return { ...state, resource: Resource.failure(action.error) }
        case ACTION_AUTHORIZE:
            return { ...state, resource: Resource.loading() }
        case ACTION_AUTHORIZE_SUCCESS:
            return { ...state, resource: Resource.of(Board.init(action.board, true)) }
        case ACTION_AUTHORIZE_FAILURE:
            return { ...state, resource: Resource.failure(action.error) }
        case ACTION_LOAD_MORE:
            return { ...state, isLoadingMore: true }
        case ACTION_LOAD_MORE_SUCCESS:
            return {
                ...state,
                resource: state.resource.transform((value) => value.append(action.items)),
                isLoadingMore: false,
            }
        case ACTION_LOAD_MORE_FAILURE:
            return { ...state, isLoadingMore: false }
    }
}

const sideEffect = async (state: BoardState, action: Action, dispatcher: React.Dispatch<Action>) => {
    switch(action.type) {
        case ACTION_GET: {
            if (state.isLoadingMore) {
                return
            }
            try {
                const subsChannelProducts = await SubsApiAction.getSubsProducts(state.userId)
                const board = await BoardApiAction.get(state.userId, subsChannelProducts)
                dispatcher(getSuccess(board, subsChannelProducts))
            } catch (e: unknown) {
                dispatcher(getFailure(e))
            }
            return
        }
        case ACTION_AUTHORIZE: {
            try {
                if (!state.subsChannelProducts) {
                    throw Error('call ACTION_GET before')
                }
                const res = await BoardApiAction.getAuthorized(state.userId, state.subsChannelProducts)
                dispatcher(authorizeSuccess(res, ))
            } catch (e: unknown) {
                dispatcher(authorizeFailure(e))
            }
            return
        }
        case ACTION_LOAD_MORE: {
            const nextPage = state.resource.valueOrNull?.nextPage
            const authorized = state.resource.valueOrNull?.authorized
            if (state.isLoadingMore || nextPage === null || authorized === null) {
                return
            }
            try {
                if (!state.subsChannelProducts) {
                    throw Error('call ACTION_GET before')
                }
                if (authorized) {
                    const res = await BoardApiAction.getAuthorized(state.userId, state.subsChannelProducts, nextPage)
                    dispatcher(loadMoreSuccess(res))
                } else {
                    const res = await BoardApiAction.get(state.userId, state.subsChannelProducts, nextPage)
                    dispatcher(loadMoreSuccess(res))
                }
            } catch (e: unknown) {
                dispatcher(loadMoreFailure(e))
            }
            return
        }
    }
}

export type BoardResult = {
    state: Resource<Board>
    authorize: () => void
    loadMore: () => void
}

export const useBoard: () => BoardResult = () => {
    const extensionState = useExtensionContext()
    const initialState: BoardState = useMemo(() => ({
        userId: extensionState.user.id,
        isLogin: extensionState.isLogin,
        resource: Resource.loading(),
        isLoadingMore: false,
    }), []) // eslint-disable-line react-hooks/exhaustive-deps
    const [state, dispatcher] = useReducer(reducer, initialState)
    const stateRef = useRef<BoardState>(initialState)
    stateRef.current = state
    const combined = useCallback((action: Action) => {
        sideEffect(stateRef.current, action, dispatcher)
        dispatcher(action)
    }, [dispatcher])
    
    useOneShot(() => {
        if (extensionState) {
            combined(get())
        } else {
            combined(getFailure(Error('require ExtensionState')))
        }
    })

    const _authorize = useCallback(() => {
        combined(authorize())
    }, [combined])

    const _loadMore = useCallback(() => {
        combined(loadMore())
    }, [ // eslint-disable-line react-hooks/exhaustive-deps
        combined,
        state.resource.valueOrNull?.nextPage,
        state.resource.valueOrNull?.authorized
    ])

    return {
        state: state.resource,
        authorize: _authorize,
        loadMore: _loadMore,
    }
}
